import { TextBox, TextBoxRaw } from "@/components/ui/TextBox";
import { RevisionBreadcrumbs } from "@/components/ui/breadcrumbs/RevisionBreadcrumbs";
import { useStoryConfigById } from "@/queries/story-configs/use-story-config-by-id";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { omit } from "lodash";
import { useNavigate } from "react-router";
import { TimestampBreadcrumbs } from "../ui/breadcrumbs/TimestampBreadcrumbs";
import { CreateStoryConfigRevision } from "./CreateStoryConfigRevision";

export function StoryConfigRevision({
  id,
  revisionNumber,
}: {
  id: string | undefined;
  revisionNumber: number | undefined;
}) {
  const { data } = useStoryConfigById(id);

  const navigate = useNavigate();

  if (!id || !data) {
    return null;
  }

  const header = (
    <Typography component={"h1"} variant="h6">
      Revision
    </Typography>
  );

  const revisions = data.revisions.toSorted(
    (r1, r2) => r1.revision - r2.revision,
  );
  const latestRevisionIdx = revisions.length - 1;

  if (revisions.length === 0) {
    return (
      <>
        <Stack
          direction={"row"}
          gap={2}
          alignItems="center"
          justifyContent={"space-between"}
        >
          {header}
          <CreateStoryConfigRevision configId={id} />
        </Stack>
        <Alert severity="info">No revisions for this story config. Create first revision.</Alert>
      </>
    );
  }

  // revision to display
  const currentRevisionIdx = revisionNumber
    ? revisions.findIndex((r) => r.revision === revisionNumber)
    : latestRevisionIdx;

  if (currentRevisionIdx === -1) {
    return (
      <>
        {header}
        <Alert
          sx={{ width: "1200px" }}
          severity="error"
        >{`Revision ${revisionNumber} not found`}</Alert>
      </>
    );
  }

  const currentRevision = revisions[currentRevisionIdx];
  const prevRevision =
    currentRevisionIdx > 0 ? revisions[currentRevisionIdx - 1] : undefined;
  const nextRevision =
    currentRevisionIdx < latestRevisionIdx
      ? revisions[currentRevisionIdx + 1]
      : undefined;

  const isLatestRevision = currentRevisionIdx === latestRevisionIdx;

  const navigation = (
    <Stack gap={1} direction={"row"}>
      <Button
        size="small"
        startIcon={<ArrowBack />}
        disabled={!prevRevision}
        onClick={() => {
          navigate(`/story-configs/${id}/revisions/${prevRevision?.revision}`);
        }}
      >
        {prevRevision
          ? `Previous revision ( ${prevRevision.revision} )`
          : "No previous revision"}
      </Button>
      <Button
        size="small"
        endIcon={<ArrowForward />}
        disabled={!nextRevision}
        onClick={() => {
          navigate(`/story-configs/${id}/revisions/${nextRevision?.revision}`);
        }}
      >
        {nextRevision
          ? `Next revision ( ${nextRevision.revision} )`
          : "No next revision"}
      </Button>
    </Stack>
  );

  return (
    <>
      <Stack
        direction={"row"}
        gap={2}
        alignItems="center"
        justifyContent={"space-between"}
      >
        {header}
        <CreateStoryConfigRevision disabled={!isLatestRevision} configId={id} />
      </Stack>
      <RevisionBreadcrumbs {...currentRevision} />
      <TimestampBreadcrumbs {...currentRevision} />
      <TextBox>
        {currentRevision.revisionNote ? (
          <Typography color={"textSecondary"}>
            {`Revision note: ${currentRevision.revisionNote}`}
          </Typography>
        ) : (
          <Typography color={"textSecondary"}>No revision note</Typography>
        )}
      </TextBox>
      {navigation}
      <TextBoxRaw>
        {JSON.stringify(
          omit(currentRevision, [
            "storyType",
            "revisionNote",
            "revision",
            "id",
            "createdAt",
            "updatedAt",
            "mainCharacter", // TODO: cleanup after migration
            "mainCharacterImageStyle", // TODO: cleanup after migration
            "randomStoryCharacters", // TODO: cleanup after migration
            "randomStoryCharactersPairs", // TODO: cleanup after migration
          ]),
          null,
          2,
        )}
      </TextBoxRaw>
      {navigation}
    </>
  );
}
